import type {
  Localized,
  ScreeningOption,
  ScreeningQuestion,
  ScreeningSet,
  ScreeningSetOutcome,
} from '@/features/fit/types';

import { kioskFetch } from './kioskFetch';

/**
 * Reads ISS's screening set — the ten stored controls — for the fit questions screen [03-us-kpw-3a AC-2,
 * AC-11].
 *
 * ⛔ **No venue, locale or other parameter is sent** [AC-2; decisions 0204, 0358 (b)]. The set is global in
 * V1, and the kiosk credential `kioskFetch` carries decides only *who may read it*. Both languages come back
 * in one payload, so a language switch never needs a second read [D4].
 *
 * ⛔ **All or nothing.** One malformed question, option or string fails the WHOLE set to `unavailable` —
 * the opposite of `openings.ts`, which drops a bad row and keeps the rest, and deliberately so: there,
 * losing one job costs one card; here, a set shown with a question missing is a recruiter's note that
 * describes a screening ISS never ran [KPW-3a § invariant]. An empty set is `unavailable` too [AC-11].
 *
 * Never throws: every failure is a value, so the screen can hand the candidate to a person [BR-K7].
 */

const QUESTIONS_PATH = '/screening/questions';

/**
 * Bounded, and short: a candidate is standing at the device, and this is a read of ten rows from the
 * kiosk's own database — nothing fans out behind it. Matches `screening.ts`'s write.
 */
const REQUEST_TIMEOUT_MS = 10_000;

function apiBaseUrl(): string {
  return import.meta.env.VITE_API_BASE_URL ?? '';
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** A usable string: present and not blank. The value itself is kept byte for byte — never trimmed [AC-2]. */
const isText = (value: unknown): value is string =>
  typeof value === 'string' && value.trim() !== '';

function readLocalized(value: unknown): Localized | undefined {
  if (!isRecord(value) || !isText(value.en) || !isText(value.es)) return undefined;
  return { en: value.en, es: value.es };
}

/** `null` is a valid answer ("none"); anything else must be a whole `{ en, es }`. */
function readOptionalLocalized(value: unknown): Localized | null | undefined {
  return value === null ? null : readLocalized(value);
}

function readOption(value: unknown): ScreeningOption | undefined {
  if (!isRecord(value) || !isText(value.key)) return undefined;
  const label = readLocalized(value.label);
  const followUp = readOptionalLocalized(value.followUp);
  if (label === undefined || followUp === undefined) return undefined;
  return { key: value.key, label, followUp };
}

const hasUniqueKeys = (items: readonly { readonly key: string }[]): boolean =>
  new Set(items.map((item) => item.key)).size === items.length;

function readQuestion(value: unknown): ScreeningQuestion | undefined {
  if (!isRecord(value) || !isText(value.key) || typeof value.label !== 'string') return undefined;
  if (value.kind !== 'single' && value.kind !== 'multi') return undefined;

  const prompt = readLocalized(value.prompt);
  const preamble = readOptionalLocalized(value.preamble);
  if (prompt === undefined || preamble === undefined) return undefined;

  /*
   * A question with no options cannot be answered, and every control is required [0364 (d)] — so shown, it
   * would hold the candidate at Continue forever. That is a broken set, not a question to skip.
   */
  if (!Array.isArray(value.options) || value.options.length === 0) return undefined;
  const options = value.options.map(readOption);
  if (options.some((option) => option === undefined)) return undefined;
  const whole = options as ScreeningOption[];
  // Answers are held by option key [AC-7]; two options sharing one would share an answer.
  if (!hasUniqueKeys(whole)) return undefined;

  return { key: value.key, label: value.label, kind: value.kind, preamble, prompt, options: whole };
}

/** Exported for its own tests: the parse is where the "never partial" half of the invariant lives. */
export function readScreeningSet(body: unknown): ScreeningSet | undefined {
  if (!isRecord(body) || !Array.isArray(body.questions) || body.questions.length === 0) {
    return undefined;
  }
  const questions = body.questions.map(readQuestion);
  if (questions.some((question) => question === undefined)) return undefined;
  const whole = questions as ScreeningQuestion[];
  if (!hasUniqueKeys(whole)) return undefined;
  return { questions: whole };
}

/**
 * The operator's half of a failed read. The candidate sees a neutral hand-off and nothing else; without this
 * line the kiosk quietly stops screening and nobody learns why. Same shape as `kioskFetch`'s refusal alert
 * (`msg` · `alert` · …) — and ⛔ no candidate detail: it prints on a shared device's console.
 */
function unavailable(
  reason: 'unreachable' | 'status' | 'unparseable',
  status?: number,
): ScreeningSetOutcome {
  console.error({
    msg: 'GET /screening/questions produced no usable screening set',
    alert: 'kiosk_screening_set_unavailable',
    reason,
    ...(status === undefined ? {} : { status }),
  });
  return { kind: 'unavailable' };
}

export async function fetchScreeningQuestions(): Promise<ScreeningSetOutcome> {
  let response: Response;
  try {
    response = await kioskFetch(`${apiBaseUrl()}${QUESTIONS_PATH}`, {
      signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
    });
  } catch {
    return unavailable('unreachable');
  }

  // A 401 has already been reported, and routed to the PIN pad, by `kioskFetch` [0245] — once is enough.
  if (response.status === 401) return { kind: 'unavailable' };
  // `200` only: a `204` or a `503` [AC-3] is "no set to show" too.
  if (response.status !== 200) return unavailable('status', response.status);

  let body: unknown;
  try {
    body = await response.json();
  } catch (error) {
    // The timeout also covers the body: a stalled stream is the network, not a malformed answer.
    const stalled =
      error instanceof DOMException &&
      (error.name === 'TimeoutError' || error.name === 'AbortError');
    return unavailable(stalled ? 'unreachable' : 'unparseable');
  }
  const set = readScreeningSet(body);
  return set === undefined ? unavailable('unparseable') : { kind: 'set', set };
}
